import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, Loader2, X, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Nav from './Nav';
import { signupUser, verifyOtp } from '../api';
import Footer from '../components/Footer';
import GoogleSignIn from './GoogleSignIn';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpVerified, setOtpVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Password strength validation
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    symbol: false
  });

  // Phone number validation
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-remove spaces from phone number
    let processedValue = value;
    if (name === 'phone') {
      processedValue = value.replace(/\s/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear specific errors when user types
    if (name === 'phone') {
      setPhoneError("");
      validatePhone(processedValue);
    }
    if (name === 'email') {
      setEmailError("");
      validateEmail(processedValue);
    }

    // Check password strength when password changes
    if (name === 'password') {
      checkPasswordStrength(processedValue);
    }
  };

  const validatePhone = (phone) => {
    // Indian phone number validation (10 digits, optionally with +91)
    const phoneRegex = /^(\+91\s?)?[6-9]\d{9}$/;
    if (phone && !phoneRegex.test(phone)) {
      setPhoneError("Please enter a valid Indian phone number (10 digits)");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    });
  };

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const passwordsMatch = () => {
    return formData.password === formData.confirmPassword && formData.password !== '';
  };

  const canSubmit = () => {
    return formData.name && 
           formData.email && 
           formData.phone && 
           !phoneError && 
           !emailError &&
           isPasswordStrong() && 
           passwordsMatch();
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    if (!validatePhone(formData.phone) || !validateEmail(formData.email)) {
      return;
    }

    if (!canSubmit()) {
      if (!isPasswordStrong()) {
        setErrorMessage("Please ensure your password meets all strength requirements");
        return;
      }
      if (!passwordsMatch()) {
        setErrorMessage("Passwords do not match");
        return;
      }
      if (phoneError) {
        setErrorMessage(phoneError);
        return;
      }
      if (emailError) {
        setErrorMessage(emailError);
        return;
      }
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const result = await signupUser(formData);
      if (result.success) {
        setShowOtpModal(true);
      } else {
        // Handle specific error cases
        if (result.message.includes("email") || result.message.includes("Email")) {
          setEmailError("This email is already registered. Please use a different email or try logging in.");
        } else if (result.message.includes("phone") || result.message.includes("Phone")) {
          setPhoneError("This phone number is already registered. Please use a different phone number.");
        } else {
          setErrorMessage(result.message || "Signup failed");
        }
      }
    } catch (error) {
      setErrorMessage("An error occurred during signup");
      console.error("Signup error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setErrorMessage("");

    const otpString = otp.join("");
    try {
      const result = await verifyOtp(formData.email, otpString);
      if (result.success) {
        setOtpVerified(true);
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setErrorMessage(result.message || "OTP verification failed");
      }
    } catch (error) {
      setErrorMessage("An error occurred during OTP verification");
      console.error("OTP verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center space-x-2 text-sm ${met ? 'text-green-400' : 'text-gray-400'}`}>
      {met ? (
        <Check size={16} className="text-green-400" />
      ) : (
        <AlertCircle size={16} className="text-gray-400" />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-black text-white mt-10 ">

        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
          <Card className="w-full max-w-md bg-black/80 backdrop-blur-xl border-gray-800 shadow-2xl">
            <CardHeader className="space-y-2 text-center">
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Create account
              </CardTitle>
              <CardDescription className="text-gray-400 text-lg">
                Fill in your details to get started
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {errorMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
                  {errorMessage}
                </div>
              )}

              {/* Google Signup at the top */}
              <div className="space-y-4">
                <GoogleSignIn
                  onError={(error) => setErrorMessage(error)}
                  buttonText="Sign up with Google"
                  className="w-full"
                />
                
                <div className="relative">
                  <Separator className="bg-gray-700" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-black px-4 text-gray-400 text-sm">or</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300 font-medium">
                    Full name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 transition-colors"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300 font-medium">
                    Email address <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`bg-gray-900/50 border text-white placeholder:text-gray-500 focus:ring-blue-500/20 transition-colors ${
                      emailError ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-blue-500'
                    }`}
                    placeholder="Enter your email"
                    required
                  />
                  {emailError && (
                    <div className="text-red-400 text-sm flex items-center space-x-1">
                      <AlertCircle size={14} />
                      <span>{emailError}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-300 font-medium">
                    Phone number <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`bg-gray-900/50 border text-white placeholder:text-gray-500 focus:ring-blue-500/20 transition-colors ${
                      phoneError ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-blue-500'
                    }`}
                    placeholder="Enter your phone number (e.g., 9876543210)"
                    required
                  />
                  {phoneError && (
                    <div className="text-red-400 text-sm flex items-center space-x-1">
                      <AlertCircle size={14} />
                      <span>{phoneError}</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Format: 10-digit number (e.g., 9876543210)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300 font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 transition-colors pr-10"
                      placeholder="Create a strong password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Requirements - Show when user starts typing */}
                  {formData.password && (
                    <div className="mt-3 p-3 bg-gray-900/30 border border-gray-700 rounded-lg space-y-2">
                      <p className="text-xs text-gray-400 font-medium mb-2">Password must contain:</p>
                      <PasswordRequirement met={passwordStrength.length} text="At least 8 characters" />
                      <PasswordRequirement met={passwordStrength.uppercase} text="One uppercase letter (A-Z)" />
                      <PasswordRequirement met={passwordStrength.lowercase} text="One lowercase letter (a-z)" />
                      <PasswordRequirement met={passwordStrength.number} text="One number (0-9)" />
                      <PasswordRequirement met={passwordStrength.symbol} text="One special character (!@#$%^&*)" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300 font-medium">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`bg-gray-900/50 border text-white placeholder:text-gray-500 focus:ring-blue-500/20 transition-colors pr-10 ${
                        formData.confirmPassword 
                          ? (passwordsMatch() ? 'border-green-500 focus:border-green-500' : 'border-red-500 focus:border-red-500')
                          : 'border-gray-700 focus:border-blue-500'
                      }`}
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  
                  {/* Password Match Indicator */}
                  {formData.confirmPassword && (
                    <div className={`flex items-center space-x-2 text-sm ${
                      passwordsMatch() ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {passwordsMatch() ? (
                        <Check size={16} />
                      ) : (
                        <AlertCircle size={16} />
                      )}
                      <span>
                        {passwordsMatch() ? 'Passwords match' : 'Passwords do not match'}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !canSubmit()}
                  className="w-full bg-white text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 className="animate-spin" size={20} />
                      <span>Creating account...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Create account</span>
                      <ArrowRight size={18} />
                    </div>
                  )}
                </Button>
              </form>

              <div className="text-center text-gray-400">
                <span>
                  Already have an account?{' '}
                  <a href="/login" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                    Sign in
                  </a>
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* OTP Verification Modal */}
        {showOtpModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="relative w-full max-w-md bg-black/95 border border-gray-800 rounded-lg shadow-2xl p-6">
              <button
                onClick={() => setShowOtpModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Verify your email
                </h2>
                <p className="text-gray-400">
                  We've sent a 6-digit code to {formData.email}
                </p>
              </div>

              {errorMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center mb-6">
                  {errorMessage}
                </div>
              )}

              {!otpVerified ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-6 gap-3">
                    {otp.map((data, index) => (
                      <Input
                        key={index}
                        type="text"
                        maxLength={1}
                        value={data}
                        onChange={e => handleOtpChange(e.target, index)}
                        onFocus={e => e.target.select()}
                        className="text-center text-lg font-bold bg-gray-900/50 border-gray-700 text-white focus:border-blue-500 focus:ring-blue-500/20 transition-colors"
                      />
                    ))}
                  </div>

                  <Button
                    onClick={handleVerifyOtp}
                    disabled={loading || otp.join("").length !== 6}
                    className="w-full bg-white text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <Loader2 className="animate-spin" size={20} />
                        <span>Verifying...</span>
                      </div>
                    ) : (
                      'Verify Email'
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                  >
                    Resend code
                  </Button>
                </div>
              ) : (
                <div className="text-center space-y-4 py-6">
                  <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Check size={32} className="text-green-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Verification Successful!</h2>
                  <p className="text-gray-400">Welcome! Your premium account is ready.</p>
                  <p className="text-gray-400">Redirecting you to login...</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
      <Footer />
    </>
  );
};

export default Signup;